import { FreaCompletedPart, FreaInitiateMultipartResponse, FreaPartUrlResponse } from './types';
import { sanitizeFileName } from './utils';

/** 16 MB parts keep a 5 GB file at ~320 parts, far under S3's 10,000-part cap. */
export const PART_SIZE_BYTES = 16 * 1024 * 1024;
export const PART_CONCURRENCY = 4;
export const PART_MAX_ATTEMPTS = 3;
export const FILE_CONCURRENCY = 2;
const RETRY_BASE_DELAY_MS = 500;

export type FreaPost = <T>(path: string, body?: unknown) => Promise<T>;

/** PUTs one part to its presigned URL and resolves with the part's ETag. */
export type PutPart = (url: string, blob: Blob, onProgress: (loadedBytes: number) => void, signal?: AbortSignal) => Promise<string>;

// XHR rather than fetch: fetch cannot report upload progress.
export const xhrPutPart: PutPart = (url, blob, onProgress, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = event => onProgress(event.loaded);
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Part upload failed with status ${xhr.status}`));
        return;
      }
      const eTag = xhr.getResponseHeader('ETag');
      if (eTag) {
        resolve(eTag);
      } else {
        reject(new Error('Part upload response has no ETag header (the bucket CORS policy must expose ETag)'));
      }
    };
    xhr.onerror = () => reject(new Error('Part upload network error'));
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(blob);
  });

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * Runs `worker` over `items` with at most `limit` in flight; stops picking up new items after the first failure.
 * Resolves only once every in-flight worker has settled, then rejects with the first failure.
 */
export const runWithConcurrency = async <T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> => {
  const state: { nextIndex: number; failed: boolean; error?: unknown } = { nextIndex: 0, failed: false };
  const runNext = async (): Promise<void> => {
    if (state.failed || state.nextIndex >= items.length) return;
    const item = items[state.nextIndex];
    state.nextIndex += 1;
    try {
      await worker(item);
    } catch (error) {
      if (!state.failed) {
        state.failed = true;
        state.error = error;
      }
      return;
    }
    await runNext();
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runNext));
  if (state.failed) throw state.error;
};

export interface UploadFileMultipartParams {
  file: File;
  sourceId: string;
  post: FreaPost;
  putPart?: PutPart;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
  partSize?: number;
  retryBaseDelayMs?: number;
}

/**
 * initiate → (part-url → PUT) per part, bounded concurrency, per-part retry → complete.
 * On a terminal failure the upload is aborted so no orphaned parts are left in Frontier's bucket.
 */
export const uploadFileMultipart = async ({
  file,
  sourceId,
  post,
  putPart = xhrPutPart,
  onProgress,
  signal,
  partSize = PART_SIZE_BYTES,
  retryBaseDelayMs = RETRY_BASE_DELAY_MS,
}: UploadFileMultipartParams): Promise<{ key: string }> => {
  const { uploadId, key } = await post<FreaInitiateMultipartResponse>('uploads/multipart/initiate', {
    sourceId,
    fileName: sanitizeFileName(file.name),
    contentType: file.type || 'application/octet-stream',
    fileSize: file.size,
  });

  const partCount = Math.max(1, Math.ceil(file.size / partSize));
  const loadedPerPart = new Array<number>(partCount).fill(0);
  const reportProgress = () => {
    const loaded = loadedPerPart.reduce((sum, bytes) => sum + bytes, 0);
    onProgress?.(file.size ? Math.min(100, Math.round((loaded / file.size) * 100)) : 0);
  };
  const completed: FreaCompletedPart[] = [];
  // Cancels sibling parts on the first terminal failure (or an external abort), so nothing is still
  // uploading when the multipart upload is aborted.
  const controller = new AbortController();
  const abortParts = () => controller.abort();
  signal?.addEventListener('abort', abortParts, { once: true });
  if (signal?.aborted) abortParts();

  const uploadPart = async (partNumber: number, attempt = 1): Promise<void> => {
    const blob = file.slice((partNumber - 1) * partSize, Math.min(partNumber * partSize, file.size));
    try {
      const { presignedUrl } = await post<FreaPartUrlResponse>('uploads/multipart/part-url', { uploadId, key, partNumber });
      const eTag = await putPart(
        presignedUrl,
        blob,
        loadedBytes => {
          loadedPerPart[partNumber - 1] = loadedBytes;
          reportProgress();
        },
        controller.signal
      );
      loadedPerPart[partNumber - 1] = blob.size;
      reportProgress();
      completed.push({ partNumber, eTag });
    } catch (error) {
      loadedPerPart[partNumber - 1] = 0;
      reportProgress();
      if (controller.signal.aborted || attempt >= PART_MAX_ATTEMPTS) throw error;
      await sleep(retryBaseDelayMs * 2 ** (attempt - 1));
      if (controller.signal.aborted) throw error;
      await uploadPart(partNumber, attempt + 1);
    }
  };

  try {
    await runWithConcurrency(
      Array.from({ length: partCount }, (_, index) => index + 1),
      PART_CONCURRENCY,
      partNumber =>
        uploadPart(partNumber).catch(error => {
          abortParts();
          throw error;
        })
    );
    await post('uploads/multipart/complete', {
      uploadId,
      key,
      parts: [...completed].sort((a, b) => a.partNumber - b.partNumber),
    });
    return { key };
  } catch (error) {
    await post('uploads/multipart/abort', { uploadId, key }).catch(() => undefined);
    throw error;
  } finally {
    signal?.removeEventListener('abort', abortParts);
  }
};
